import type { ExtractedStatement } from '../../model/extractedStatements';

import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

/**
 * Single shared truncation limit for the statement excerpt displayed
 * throughout the reference-request wizard. The expert sees the same
 * truncation everywhere — `StatementTriageRow` (selection),
 * `SearchingStatementRow` (searching), and the review group header —
 * so a statement that fits on one line in selection still fits on
 * one line in review.
 */
export const STATEMENT_SUMMARY_TRUNCATE_LIMIT = 140;

export function truncateStatementText(text: string): string {
  if (text.length <= STATEMENT_SUMMARY_TRUNCATE_LIMIT) {
    return text;
  }

  return `${text.slice(0, STATEMENT_SUMMARY_TRUNCATE_LIMIT - 1).trimEnd()}…`;
}

interface StatementSummaryProps {
  statement: ExtractedStatement;
  /**
   * Hard cap on rendered lines. Omitted everywhere the excerpt should
   * wrap freely (selection, searching, review). The provide list passes
   * a small value so a long statement can't make its row tall enough to
   * push the next statement's row below the fold — that pushed experts
   * to publish thinking one statement was the whole job.
   */
  numberOfLines?: number;
}

/**
 * Shared visual atom for an extracted statement inside the
 * reference-request wizard. Renders just the truncated direct
 * statement text. Intentionally chrome-less: each wizard stage owns
 * its outer wrapper (Checkbox row, searching card with progress bar,
 * left-rail review-group header) and embeds this atom as the
 * consistent content block — so the statement text always looks
 * identical across stages.
 */
export function StatementSummary({ statement, numberOfLines }: StatementSummaryProps) {
  return (
    <VStack className="min-w-0 flex-1" space="xs">
      <Text
        size="sm"
        className="text-typography-900"
        numberOfLines={numberOfLines}
        ellipsizeMode="tail">
        {truncateStatementText(statement.directStatement)}
      </Text>
    </VStack>
  );
}
